// 設備詳細頁（SPEC DEV-05 / DEV-10「設備詳細頁分頁」；VMS UX 評審 2026-10-13 P1-6）：最多 7 個分頁，網址 `?tab=`（舊值轉址，detailTabs.ts）。
//   概覽：第一排只有狀態摘要（在線 · 最後上線 · 韌體（最新）· 儲存 ▸），下面是機種、所在路徑、儲存一行摘要；不放會改變設備狀態的按鈕
//   即時與回放（攝影機、通道）：單路即時＋警報器 / 補光燈控制列（響警報器第 2 級確認）＋〔在回放頁開啟〕
//   通道（NVR / Station / 基地台本體）：通道、子設備（kinio_subg）、現場設備（PoE）、節點（Node Host）
//   設定：設定表單＋智慧分析、AI 指派、感測器關聯三個區
//   錄影與儲存：錄影政策、雲端錄影（含生效來源）；有碟的另有實體碟表、儲存池、保存天數算法
//   健康與韌體：連線、憑證、訊號、溫度（沒回報的列不畫）、遠端診斷授權、韌體
//   進階：技術（owner / admin，能力清單、媒體狀態、設備 ID 收在「複製給客服」）、位置、稽核、管理（維護區：重新開機、解除綁定）
//   終端機（Kinio Node）
import { usePageSubject } from '../../lib/pageTitle';
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlarmActions } from '../../components/AlarmActions';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { KinioError, getShadow, vms, type VmsFacadeEvent } from '@kinio/web-sdk';
import { useDevicesForHome } from '../../lib/useDevices';
import { OutOfScopeHint } from '../../components/OutOfScopeHint';
import { can, canManage, fullName, sensorKind, type VmsDevice } from '../../lib/devices';
import { changeWifiOffered } from '../../lib/changeWifi';
import { macLabel, modelDisplay } from '../../lib/devices';
import { AccessInfo } from '../../components/AccessInfo';
import { deviceApi, deviceCommand, diskKind, fmtBytes, poolUsage, smartState, storageHealth, type DiskInfo, type PoolRow, type StorageHealth } from '../../lib/deviceApi';
import { smoothRetention } from '../../lib/bitrateAvg';
import { OverwriteLabel } from './OverwriteLabel';
import { browserTz, formatDateTime } from '../../lib/tz';
import { useTzResolver } from '../../lib/useTz';
import { LiveTile } from '../live/LiveTile';
import { PoeApprovals } from './PoeApprovals';
import { SettingsForm, useDeviceSettings } from './SettingsForm';
import { hasAnalytics, hideRows, layoutTiers } from './settingsTiers';
import { camState } from '../../lib/liveStatus';
import { ChannelCopy } from './ChannelCopy';
import { ChannelMatrix } from './ChannelMatrix';
import { DiagGrants } from './DiagGrants';
import { LinkDialog } from './LinkDialog';
import { PreviewSnapshot } from '../live/PreviewSnapshot';
import { EpisodeNote, classify, deviceToken, eventIcon, type VmsEventClass } from '../events/EventsPage';
import { SubgPanel } from './SubgJoin';
import { cloudModeUi, deviceModeUi, type PolicyView, exclusiveRecording, recordingTargetConfirm, cloudAppliedOf, cloudApplyStatus, cloudReasonText, cloudSourceText, cloudStateText, cloudUnsupported } from './recPolicy';
import { Dialog } from '../../components/Dialog';
import { useConfirm } from '../../components/ConfirmDialog';
import { AiTab, AuditTab, ConnectionHealth, FirmwareTab, LocationTab, NetworkInterfaces, ReportThisDevice, SupportInfo, useCanAudit, useFwStatus } from './DeviceTabs';
import { DeviceControls } from './DeviceControls';
import { controlCaps } from '../../lib/caps';
import { PoePorts } from './PoePorts';
import { DeviceEnergyBlock } from '../energy/DeviceEnergy';
import { poeOf } from './poe';
import { HostNodes } from './HostNodes';
import { hostNodesOf } from './nodes';
import { NodeTerminal } from './NodeTerminal';
import { isKinioNode } from './consoleRelay';
import { NodeUart } from './NodeUart';
import { ttlName } from './ttlTarget';
import { eventName } from '../../lib/eventName';
import { deviceEvents } from '../../lib/facade';
import { pairPhases } from './eventPairs';
import { playbackHref } from '../../lib/links';
import { Time } from '../../components/Time';
import { detailTabs, resolveTab, type DetailTab } from './detailTabs';
import { useDevicePath } from './path';
import { durationText, retentionText } from '../../lib/duration';

export function deviceHref(d: Pick<VmsDevice, 'homeId' | 'aid'>): string {
  return `/devices/${encodeURIComponent(d.homeId)}/${encodeURIComponent(d.aid)}`;
}

/** 分頁裡的一個區（有錨點；舊網址或 `#區名` 進來時捲到這裡）。 */
import { ChannelRecHealth, HostRecHealth } from './RecHealth';
import { isLocalRecorder } from '../playback/view';

function Section({ id, title, children, aside }: { id: string; title: string; children: React.ReactNode; aside?: boolean }) {
  // aside：設定分頁裡跟設定表單右欄對齊的一般區塊（AI、智慧分析、連動；第二輪 R2-P1-5「AI 區塊做成一般區塊」）
  return (
    <section id={id} className={'ddev-section' + (aside ? ' ddev-aside card' : '')} data-ddev-section={id}>
      <h2 className="section-title">{title}</h2>
      {children}
    </section>
  );
}

export function DeviceDetailPage() {
  const { t } = useTranslation();
  const { homeId = '', aid = '' } = useParams();
  const [sp, setSp] = useSearchParams();
  const loc = useLocation();
  // 深連結不受目前範圍限制（P0-2）：範圍外但還有權限 → 直接查那個家戶、頁頂提示可切範圍
  const { tree, results, loading, outOfScope } = useDevicesForHome(homeId, aid);
  const all = tree.flatMap((d) => [d, ...d.children]);
  const dev = all.find((d) => d.homeId === homeId && d.aid === aid);
  usePageSubject(dev?.name); // 分頁標題「3KPT · 設備 · LivingStyle VMS」（第二輪 B2-22）
  const home = results.find((r) => r.home.home_id === homeId)?.home;
  const role = home?.role ?? '';
  const canAudit = useCanAudit(dev ?? ({ homeId } as VmsDevice));
  const tzOf = useTzResolver();
  const want = resolveTab(sp.get('tab'), loc.hash);
  const shadow = useQuery({
    queryKey: ['shadow', homeId, aid],
    queryFn: () => getShadow(dev!.apiEndpoint, homeId, dev!.parentAid ?? aid),
    enabled: !!dev,
    // 現場設備區看 PoE 孔狀態（設備 5 秒內報開關 / 供電變化）→ 通道分頁刷快一點
    refetchInterval: want?.tab === 'channels' ? 10_000 : 30_000,
    retry: false,
  });
  // 設定表單（設定 / 智慧分析 / 技術共用同一份快取）：設定分頁要看這台有沒有 kinio_analytics
  const settings = useDeviceSettings(dev);
  // 舊網址或錨點指定的區：分頁畫好後捲到那一區
  const section = want?.section;
  useEffect(() => {
    if (!section || !dev) return;
    const id = window.setTimeout(() => document.getElementById(section)?.scrollIntoView({ block: 'start' }), 50);
    return () => window.clearTimeout(id);
  }, [section, dev, want?.tab]);
  if (!dev) {
    return (
      <section>
        <p className="muted">{loading ? t('common.loading') : t('ddev.not_found')}</p>
        <Link to="/devices">{t('ddev.back')}</Link>
      </section>
    );
  }
  // 預估保存天數用 24 小時平均位元率（跟總覽同一個函式、同一個值；第六輪 B6-07）
  const storage = smoothRetention(dev.key, storageHealth(shadow.data?.state?.reported?.kinio_storage as Record<string, unknown> | undefined));
  // 管理動作（解綁、OTA、技術設定、通道複製…）只看「我的設備」的 can_manage；資料動作看 actions（SPEC #device-permissions-ui）
  const manager = canManage(dev);
  const manage = !dev.parentAid && manager;
  // Sub-GHz 閘道（protocol kinio_subg）：有這個能力才有「子設備」區（DEV-04）
  const subg = !dev.parentAid && (!!shadow.data?.state?.reported?.kinio_subg || !!dev.capabilities?.includes('kinio_subg'));
  // 現場設備（STN-05）：Station 報了 kinio_station.poe_ports（有 PoE PSE 明細）才有；桌面 Station 不報 → 沒有這區（ST-CTL-05）
  const reported = shadow.data?.state?.reported as Record<string, unknown> | undefined;
  const site = !dev.parentAid && !!poeOf(reported);
  // 節點（NODE-01）：Node Host（KST 兼任或 kinio-node-host）報了 kinio_node_host.nodes 才有
  const hostNodes = dev.parentAid ? null : hostNodesOf(reported);
  const nodeHost = !!hostNodes;
  // 終端機（NODE-05、SPEC #node-ttl-vms-terminal）：Kinio Node 本體，有 device.read 就能唯讀（寫入只限管理角色）；在進階 › 診斷、預設摺疊、不自動連線（R3-P0-4、B4-14）
  const nodeConsole = !dev.parentAid && isKinioNode(dev.model) && can(dev, 'device.read');
  // 舊網址指到某一區、shadow 還在讀時先保留那個分頁（讀完沒有那一區才回概覽）
  const waiting = shadow.isLoading && (section === 'site' || section === 'nodes' || section === 'subg');
  // 工位模式的 Station（SPEC #vms-web-s5-19-1 STN-01，Eddy 2026-10-16）：不錄影、不開通道——沒有「錄影與儲存」分頁；
  // 「通道」分頁只在真的接了 Kinio Node（節點清單不是空的）或有現場設備時才有
  const seat = !!dev.seatMode && !dev.parentAid;
  const tabs = detailTabs({
    video: dev.isCamera,
    host: seat
      ? site || waiting || (hostNodes?.length ?? 0) > 0
      : (dev.children.length > 0 && !subg) || subg || site || nodeHost || waiting,
    recording: !seat && (dev.isCamera || !!storage || layoutTiers(settings.data, 'recording').length > 0),
  });
  const tab: DetailTab = want && tabs.includes(want.tab) ? want.tab : 'overview';
  const fwVisible = !dev.parentAid && !!role; // 韌體是有連線的設備本體的；家戶成員才看得到（經分享的沒有）
  const analytics = hasAnalytics(settings.data);
  // 攝影機本體：錄影與儲存最上面的「記憶卡」列已經管 sd_enabled / sd_mode（擁有者可改），設定表單不重複畫（第四輪 B4-04）
  const recHide = dev.isCamera && !dev.parentAid && role === 'owner' ? SD_POLICY_KEYS : undefined;
  const recSettings = hideRows(layoutTiers(settings.data, 'recording'), recHide).length > 0;
  return (
    <section data-device-detail={dev.aid}>
      <p>
        <Link to="/devices">← {t('ddev.back')}</Link>
      </p>
      {outOfScope && <OutOfScopeHint homeId={homeId} kind="device" />}
      <h1 className="page-title">
        <span className={`dot ${dev.presence}`} />
        {fullName(dev, tree)}
      </h1>
      {macLabel(dev.macSuffix) && (
        <p className="muted" data-mac-suffix={dev.macSuffix} style={{ marginTop: -8 }}>
          {macLabel(dev.macSuffix)}
        </p>
      )}
      <div className="dtabs" role="tablist">
        {tabs.map((x) => (
          <button key={x} role="tab" aria-selected={x === tab} data-tab={x} onClick={() => setSp(x === 'overview' ? {} : { tab: x }, { replace: true })}>
            {t(`ddev.tab_${x}`)}
          </button>
        ))}
      </div>
      <div className="dtab-body">
        {tab === 'overview' && (
          <>
            <StatusLine dev={dev} storage={storage} fw={fwVisible} recording={tabs.includes('recording')} goto={(x) => setSp({ tab: x }, { replace: true })} />
            {/* 用電（plan SPEC NRG-V8）：插座、Matter 插座 / 電表、接在 Station 孔上的攝影機；不是量測點不畫 */}
            <DeviceEnergyBlock dev={dev} />
            {dev.access?.source !== 'own' && <AccessInfo access={dev.access} />}
            <Overview dev={dev} tree={tree} storage={storage} goto={(x) => setSp({ tab: x }, { replace: true })} />
          </>
        )}
        {tab === 'live' && (
          <div style={{ maxWidth: 960 }}>
            <LiveTile
              cam={dev}
              label={fullName(dev, tree)}
              stream="main"
              // PTZ / 對講依能力（lib/caps.ts）：獨立設備看生效能力（kinio_ptz / kinio_audio）、通道看 kinio_channel.abilities
              ptz={controlCaps(dev).ptz}
              talk={
                // 對講入口（LIVE-07）：有對講能力、這台給了 media.talk（伺服器另外驗）
                controlCaps(dev).talk && can(dev, 'media.talk')
              }
            />
            {/* 警報器 / 補光燈在這裡的控制列（LIVE-07）：響警報器收在〔⋯〕、第 2 級確認 */}
            <div className="toolbar wrap">
              <Link className="btn btn-sm" to={playbackHref(dev.key, Math.floor(Date.now() / 1000) - 60)}>
                {t('ddev.open_playback')}
              </Link>
              <DeviceControls dev={dev} compact sounding={(reported?.kinio_siren as { sounding?: boolean } | undefined)?.sounding === true} />
            </div>
          </div>
        )}
        {tab === 'channels' && (
          <>
            {/* 本體有警報器 / 補光燈（例 NVR 的蜂鳴器）：沒有即時分頁，控制列放在通道分頁最上面 */}
            <DeviceControls dev={dev} compact />
            {subg && <SubgPanel dev={dev} />}
            {dev.children.length > 0 && !subg && (
              <Section id="channels" title={t('ddev.tab_channels')}>
                {/* key = 這台設備：換設備時支援狀態、清單、進行中的流程都重來（不帶上一台的卡） */}
                <PoeApprovals key={dev.key} dev={dev} canManage={manager} initialOpen={sp.get('poe') === '1'} />
                <Channels dev={dev} />
                <ChannelMatrix dev={dev} canTech={manager} />
                {manager && <ChannelCopy dev={dev} />}
              </Section>
            )}
            {site && (
              <Section id="site" title={t('ddev.tab_site')}>
                <PoePorts dev={dev} reported={reported} onChanged={() => void shadow.refetch()} />
              </Section>
            )}
            {nodeHost && (
              <Section id="nodes" title={t('ddev.tab_nodes')}>
                <HostNodes dev={dev} reported={reported} tree={tree} />
              </Section>
            )}
            {waiting && <p className="muted">{t('common.loading')}</p>}
          </>
        )}
        {tab === 'settings' && (
          <>
            <SettingsForm dev={dev} />
            {analytics && (
              <Section id="analytics" title={t('ddev.tab_analytics')}>
                <SettingsForm dev={dev} view="analytics" />
              </Section>
            )}
            {dev.isCamera && (
              <Section id="ai" title={t('ddev.tab_ai')} aside>
                <AiTab dev={dev} />
              </Section>
            )}
            {sensorKind(dev) && (
              <Section id="links" title={t('ddev.tab_links')} aside>
                <Links dev={dev} />
              </Section>
            )}
          </>
        )}
        {tab === 'recording' && (
          <>
            {/* NVR / Kinio Station 的通道：最上面一區「錄影健康」（kinio_recording.rec_health，SPEC #vms-web-rec-health） */}
            {dev.parentAid && dev.isCamera && isLocalRecorder(all.find((d) => d.homeId === homeId && d.aid === dev.parentAid)) && (
              <Section id="rec-health" title={t('rech.title')}>
                <ChannelRecHealth dev={dev} host={all.find((d) => d.homeId === homeId && d.aid === dev.parentAid)} />
              </Section>
            )}
            {dev.isCamera && <RecordingPolicy dev={dev} owner={role === 'owner'} />}
            {/* NVR / Station（有實體儲存、不是攝影機）：狀態摘要 → 碟表 → 儲存池在前，設定摺疊在後（第三輪 R3-P1-2、B3-10）；
                攝影機照舊：錄影政策 → 設定 → 儲存 */}
            {storage && !dev.isCamera && (
              <Section id="storage" title={t('ddev.storage')}>
                <Health storage={storage} dev={dev.parentAid ? undefined : dev} canTest={manager} tz={tzOf(dev.homeId)} />
                {/* 主機：儲存摘要下面「每路錄影健康」表（中斷的排最前面） */}
                {!dev.parentAid && isLocalRecorder(dev) && <HostRecHealth dev={dev} />}
              </Section>
            )}
            {/* 錄影 / 儲存的設定項目只在這裡（設定分頁不再有這兩個子區，第二輪 R2-P1-5） */}
            {recSettings &&
              (dev.isCamera ? (
                <Section id="rec-settings" title={t('ddev.rec_settings')}>
                  <SettingsForm dev={dev} view="recording" hide={recHide} />
                </Section>
              ) : (
                <details className="card ddev-rec-settings" id="rec-settings" data-testid="ddev-rec-settings" open={section === 'rec-settings'}>
                  <summary>
                    <strong>{t('ddev.rec_settings_fold', {
                      count: layoutTiers(settings.data, 'recording').reduce((n, x) => n + x.common.length + x.advanced.reduce((m, g) => m + g.rows.length, 0), 0),
                    })}</strong>
                  </summary>
                  <SettingsForm dev={dev} view="recording" />
                </details>
              ))}
            {storage && dev.isCamera && (
              <Section id="storage" title={t('ddev.storage')}>
                <Health storage={storage} dev={dev.parentAid ? undefined : dev} canTest={manager} tz={tzOf(dev.homeId)} />
              </Section>
            )}
          </>
        )}
        {tab === 'events' && <DeviceEvents dev={dev} />}
        {tab === 'health' && (
          <>
            <ConnectionHealth dev={dev.parentAid ? (all.find((d) => d.homeId === homeId && d.aid === dev.parentAid) ?? dev) : dev} reported={reported} />
            <NetworkInterfaces reported={reported} />
            {role && <DiagGrants dev={dev} canDecide={manager} />}
            {fwVisible && (
              <Section id="firmware" title={t('ddev.tab_firmware')}>
                <FirmwareTab dev={dev} owner={manager} />
              </Section>
            )}
          </>
        )}
        {tab === 'advanced' && (
          <>
            {/* 進階分頁順序（SPEC #vms-web-327 進階分頁順序與長度；第二輪 R2-P1-4）：維護（預設摺疊）→ 技術 → 位置 → 稽核（最近 20 筆） */}
            {manage && (
              <details className="card maint-zone" id="manage" data-testid="ddev-maintenance" open={section === 'manage'}>
                <summary>
                  <strong>{t('ddev.maintenance')}</strong> <span className="muted small">{t('ddev.maintenance_sub')}</span>
                </summary>
                <Manage dev={dev} />
              </details>
            )}
            {nodeConsole && <DiagZone key={dev.key} dev={dev} tree={tree} initiallyOpen={section === 'diag'} />}
            {manager ? (
              <Section id="technical" title={t('ddev.tab_technical')}>
                <SettingsForm dev={dev} view="technical" />
                <SupportInfo dev={dev} />
              </Section>
            ) : (
              // 複製給客服（設備 ID 等）看得到這台就有：用 Claude / nodectl 看 log 要 AID，不該要 admin（Eddy 2026-10-19）；技術設定項目仍只對 owner / admin
              <SupportInfo dev={dev} />
            )}
            <ReportThisDevice dev={dev} />
            <Section id="location" title={t('ddev.tab_location')}>
              <LocationTab dev={dev} />
            </Section>
            {canAudit && (
              <Section id="audit" title={t('ddev.tab_audit')}>
                <AuditTab dev={dev} />
              </Section>
            )}
          </>
        )}
      </div>
    </section>
  );
}

/**
 * Kinio Node 診斷區（SPEC #node-ttl-vms-terminal；第四輪 B4-14、R4-P1-6）：預設摺疊，`#diag` 錨點帶進來（設備清單〔終端機〕、舊 ?tab=console）才直接展開。
 * 摺起來等於離開這一區：終端機元件卸載、連線關掉（下次展開一樣從唯讀、不連線開始）。
 */
export function DiagZone({ dev, tree = [], initiallyOpen }: { dev: VmsDevice; tree?: VmsDevice[]; initiallyOpen: boolean }) {
  const { t } = useTranslation();
  // 標題列順帶寫接的是哪台（第十三輪 R13-P2-2；名字跟概覽「UART（TTL）接著的設備」同一個函式）
  const dut = ttlName(dev, tree);
  const [open, setOpen] = useState(initiallyOpen);
  useEffect(() => {
    if (initiallyOpen) setOpen(true);
  }, [initiallyOpen]);
  return (
    <details className="card diag-zone" id="diag" data-ddev-section="diag" data-testid="ddev-diag" open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary>
        <strong>{t('ddev.adv_diag')}</strong> <span className="muted small">{t('ddev.adv_diag_sub')}</span>
        {dut && (
          <span className="muted small" data-testid="ddev-diag-dut">
            {' · '}
            {t('ddev.adv_diag_dut', { name: dut })}
          </span>
        )}
      </summary>
      {open && <NodeTerminal dev={dev} />}
    </details>
  );
}

/** 概覽第一排（P1-6）：只放狀態摘要，不放會改變設備狀態的按鈕。 */
function StatusLine({
  dev,
  storage,
  fw,
  recording,
  goto,
}: {
  dev: VmsDevice;
  storage: StorageHealth | null;
  fw: boolean;
  /** 有「錄影與儲存」分頁（工位 Station 沒有）：儲存狀態才連過去 */
  recording: boolean;
  goto(tab: DetailTab): void;
}) {
  const { t } = useTranslation();
  const s = useFwStatus(dev, fw);
  const ver = s.data?.current_version || dev.firmware;
  return (
    <p className="ddev-status" data-testid="ddev-status">
      <span className={`dot ${dev.presence}`} />
      <strong>{t(`devices.${dev.presence}`)}</strong>
      {dev.lastSeen && (
        <>
          {' · '}
          {t('ddev.last_seen')} <Time at={dev.lastSeen} rel />
        </>
      )}
      {ver && (
        <>
          {' · '}
          <button type="button" className="link-btn" onClick={() => goto('health')} data-testid="ddev-status-fw">
            {t('ddev.fw_short', { v: ver })}
            {s.data ? (s.data.update_available ? t('ddev.fw_has_update') : t('ddev.fw_is_latest')) : ''}
          </button>
        </>
      )}
      {storage && (
        <>
          {' · '}
          {recording ? (
            <button type="button" className="link-btn" onClick={() => goto('recording')} data-testid="ddev-status-storage">
              {t('ddev.storage')} {t(`health.level_${storage.level}`)} ▸
            </button>
          ) : (
            <span data-testid="ddev-status-storage">
              {t('ddev.storage')} {t(`health.level_${storage.level}`)}
            </span>
          )}
        </>
      )}
    </p>
  );
}

/** NVR / Kinio Station 的通道摘要「3 路 · 2 路離線」（跟左樹、影像牆同一個狀態函式；SPEC #vms-web-327，第二輪 R2-P1-5）。 */
export function channelSummary(dev: Pick<VmsDevice, 'presence' | 'children'>): { total: number; offline: number } | null {
  if (!dev.children.length) return null;
  const offline = dev.children.filter((c) => camState(c, dev.presence) !== 'online' && camState(c, dev.presence) !== 'unknown').length;
  return { total: dev.children.length, offline };
}

function Overview({ dev, tree, storage, goto }: { dev: VmsDevice; tree: VmsDevice[]; storage: StorageHealth | null; goto(tab: DetailTab): void }) {
  const { t } = useTranslation();
  const pathOf = useDevicePath();
  const rows: [string, React.ReactNode][] = [
    [t('devices.col_model'), modelDisplay(t, dev)],
    [t('ddev.path'), pathOf(dev)],
  ];
  // Kinio Node：UART1 接著哪台設備（NODE-06；第十一輪 R11-P1-2，第 103 列）——façade 回來才畫，不先寫「還沒設定」
  if (!dev.parentAid && isKinioNode(dev.model) && dev.ttlTarget !== undefined)
    rows.push([t('nuart.row'), <NodeUart dev={dev} tree={tree} onFirmware={() => goto('health')} />]);
  const ch = channelSummary(dev);
  if (ch)
    rows.push([
      t('ddev.tab_channels'),
      <span data-testid="ddev-ch-summary">
        {t('ddev.ch_summary', { count: ch.total })}
        {ch.offline ? <span className="error"> · {t('ddev.ch_summary_off', { count: ch.offline })}</span> : null}{' '}
        <button type="button" className="link-btn" onClick={() => goto('channels')}>
          {t('ddev.ch_view')}
        </button>
      </span>,
    ]);
  // SD 卡插著但沒回報容量（第三輪 R3-P1-4）：寫「SD 卡 · 讀不到容量（設備沒有回報）ⓘ」，不寫「未回報 · 讀不到容量」
  const sdNoCap = !!storage && storage.level === 'unknown' && (storage.sdCard || storage.disks.some((d) => diskKind(d) === 'sd')) && !storage.totalBytes;
  if (storage)
    rows.push([
      t('ddev.storage'),
      sdNoCap ? (
        <span data-testid="ddev-sd-nocap" title={t('ddev.sd_no_capacity_tip')}>
          {t('ddev.sd_no_capacity')} <span aria-hidden>ⓘ</span>
        </span>
      ) : (
        <StorageSummary s={storage} seat={!!dev.seatMode && !dev.parentAid} />
      ),
    ]);
  const table = (
    <table className="grid dinfo">
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k}>
            <th scope="row">{k}</th>
            <td>{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
  if (!dev.isCamera) return table;
  // 攝影機 / 通道的概覽：右側最近一張事件快照＋〔看即時〕〔回放〕（第三輪 R3-P1-4：原本只有 3 列資料、下面一大片空白）；
  // 左側資料下面放最近 3 筆事件（第五輪 R5-P1-7：下半頁不再空著）
  return (
    <div className="ddev-overview">
      <div className="ddev-overview-left">
        {table}
        <RecentEvents dev={dev} onAll={() => goto('events')} />
      </div>
      <div className="ddev-snap card" data-testid="ddev-snap">
        <div className="ddev-snap-img">
          <PreviewSnapshot cam={dev} />
          <span className="muted small">{t('ddev.no_snapshot')}</span>
        </div>
        <div className="toolbar">
          <button className="btn btn-sm btn-primary" onClick={() => goto('live')} data-testid="ddev-go-live">
            {t('ddev.watch_live')}
          </button>
          <Link className="btn btn-sm" to={playbackHref(dev.key, Math.floor(Date.now() / 1000) - 600)} data-testid="ddev-go-playback">
            {t('live.watch_playback')}
          </Link>
        </div>
      </div>
    </div>
  );
}

// 連線類（連線品質回報、上線 / 離線）不算「最近事件」（第七輪 B7-15：3KPT 最近事件仍列兩筆「設備上線」）；上下線在概覽的狀態列已經看得到
const RECENT_HIDE = new Set(['kinio_system.connection_report', 'thing.online', 'thing.offline', 'online', 'offline']);
export function recentHidden(key: string, cls?: string | null): boolean {
  return RECENT_HIDE.has(key) || cls === 'connection' || /\.(online|offline)$/.test(key);
}

/** 概覽的最近事件（最近 7 天、3 筆；點了開事件詳細）。 */
function RecentEvents({ dev, onAll }: { dev: VmsDevice; onAll(): void }) {
  const { t, i18n } = useTranslation();
  const tz = useTzResolver()(dev.homeId);
  const q = useQuery({
    queryKey: ['ddev-recent-events', dev.homeId, dev.aid],
    staleTime: 60_000,
    retry: false,
    queryFn: () => {
      const now = Date.now() / 1000;
      return deviceEvents(dev.apiEndpoint, dev.homeId, dev.aid, now - 7 * 86400, now, 1);
    },
  });
  // 連線品質回報（WebRTC 遙測，看一次即時就有一筆）不算「最近事件」；其他系統事件寫具體種類（第六輪 R6-P2-7：原本只寫「系統」）
  // 同一次的開始＋結束只算一筆（第八輪 R8-P1-1）
  const list = pairPhases((q.data ?? []).filter((e) => !recentHidden(e.key, (e as { class?: string | null }).class))).slice(0, 3);
  return (
    <div className="ddev-recent" data-testid="ddev-recent-events">
      <h3>{t('ddev.recent_events')}</h3>
      {q.isLoading && <p className="muted small">{t('common.loading')}</p>}
      {q.isSuccess && list.length === 0 && <p className="muted small">{t('ddev.recent_none')}</p>}
      {list.length > 0 && (
        <ul className="plain-list">
          {list.map(({ e, ep }) => (
            <li key={e.event_id} className="small">
              <Link to={`/events/${encodeURIComponent(dev.homeId)}/${encodeURIComponent(e.event_id)}`}>{eventName(t, e.key, e.class ?? undefined, e.fields, dev.aid)}</Link>
              <span className="muted"> · {formatDateTime(Date.parse(e.occurred_at) / 1000, tz, i18n.language)}</span>
              {ep && <EpisodeNote ep={ep} />}
            </li>
          ))}
        </ul>
      )}
      <button type="button" className="link-btn small" onClick={onAll}>
        {t('ddev.recent_all')}
      </button>
    </div>
  );
}

/** seat：工位模式的 Station（不錄影）——寫「磁碟空間」，不寫錄影空間 / 覆寫 / 預估保存天數（SPEC #vms-web-s5-19-1，Eddy 2026-10-16） */
export function StorageSummary({ s, seat }: { s: StorageHealth; seat?: boolean }) {
  const { t } = useTranslation();
  // 記憶卡循環覆寫中：不畫 100% 的用量條（看起來像滿了）
  const used = s.totalBytes && s.freeBytes !== undefined && !s.sdLooping ? 1 - s.freeBytes / s.totalBytes : undefined;
  return (
    <span className="storage-sum" data-storage-level={s.level}>
      <span className={`dot health-${s.level === 'unknown' ? 'warn' : s.level}`} />
      {t(`health.level_${s.level}`)}
      {(s.physical || !s.totalBytes) && s.disks.some((d) => diskKind(d) && diskKind(d) !== 'flash') && (
        <>
          {' · '}
          <span data-disks-sum>
            {/* 內建 flash（kind: flash，只回報用量）不算實體碟、不進摘要（app/vms#3）；容量沒報就只寫種類（SD 卡攝影機，第二輪 R2-P1-5） */}
            {s.disks
              .filter((d) => diskKind(d) && diskKind(d) !== 'flash')
              .map((d) => {
                const size = d.total_bytes ?? d.totalBytes;
                return `${t(`health.kind_${diskKind(d) ?? 'disk'}`)}${size ? ' ' + fmtBytes(size) : ''}`;
              })
              .join(' + ')}
          </span>
        </>
      )}
      {' · '}
      {/* 容量讀不到時寫一句話，不寫「— / — 可用」 */}
      {s.totalBytes && s.sdLooping ? (
        <span data-testid="sd-looping">{t('health.sd_looping', { total: fmtBytes(s.totalBytes) })}</span>
      ) : s.totalBytes ? (
        <>
          {t(seat ? 'health.disk_space' : 'health.rec_space')} {capText(t, s.totalBytes, s.freeBytes)}
        </>
      ) : (
        <span data-testid="storage-space-unknown">{t('health.space_unknown')}</span>
      )}
      {used !== undefined && (
        <span className="meter" aria-hidden>
          <span style={{ width: `${Math.round(used * 100)}%` }} />
        </span>
      )}
      {!seat && s.overwriteMode && (
        <>
          {' · '}
          <OverwriteLabel m={s.overwriteMode} />
        </>
      )}
      {!seat &&
        s.retentionDays !== undefined &&
        (() => {
          // 不到 1 天寫「不到 1 天（約 N 小時）」、警示色（第十二輪 B12-05）
          const r = retentionText(t, s.retentionDays, 'retention');
          return (
            <>
              {' · '}
              <span className={r.short ? 'warn-text' : undefined} data-testid="storage-retention" data-short={r.short ? '1' : undefined}>
                {r.text}
              </span>
            </>
          );
        })()}
      {s.reasons.length > 0 && <> · {s.reasons.map((r) => t(`health.reason_${r}`, { defaultValue: t('health.reason_other') })).join(t('common.list_sep'))}</>}
    </span>
  );
}

const num = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** SMART 格子下面的小字：溫度、通電時數、有問題的扇區數、上次自我測試（結果、時間、是不是每週自動；SPEC #storage-smart） */
function SmartDetail({ d, tz }: { d: DiskInfo; tz: string }) {
  const { t, i18n } = useTranslation();
  const parts: string[] = [];
  if (num(d.smart_temp_c)) parts.push(t('health.smart_temp', { c: d.smart_temp_c }));
  if (num(d.smart_power_on_hours)) parts.push(t('health.smart_poh', { h: d.smart_power_on_hours.toLocaleString() }));
  const bad = [d.smart_realloc, d.smart_pending, d.smart_uncorrectable];
  if (bad.some((x) => num(x) && x > 0))
    parts.push(t('health.smart_sectors', { realloc: d.smart_realloc ?? 0, pending: d.smart_pending ?? 0, uncorr: d.smart_uncorrectable ?? 0 }));
  const st = d.smart_test;
  const test =
    st && st.state
      ? st.state === 'running'
        ? t('health.smart_test_running')
        : st.state === 'aborted'
          ? t('health.smart_test_aborted')
          : st.result === 'fail'
            ? t('health.smart_test_fail')
            : st.result === 'pass'
              ? t('health.smart_test_pass')
              : null
      : null;
  if (!parts.length && !test) return null;
  const when = test && st?.state !== 'running' && num(st?.at) && st.at > 0 ? formatDateTime(st.at, tz, i18n.language) : '';
  const auto = st?.auto === true; // 契約是 bool；壞值不標
  return (
    <div className="muted small" data-smart-detail>
      {parts.join(' · ')}
      {test && (
        <div data-smart-test={st?.state === 'done' ? st.result : st?.state} data-smart-test-auto={auto ? '' : undefined}>
          {test}
          {when && <> · {when}</>}
          {auto && <> · {t('health.smart_test_weekly')}</>}
        </div>
      )}
    </div>
  );
}

/** 自我測試（short）：owner / admin；只有 SMART 讀得到（不是 unknown）的碟才有（USB 橋不支援時設備回 501） */
function SmartTestButton({ dev, diskNo }: { dev: VmsDevice; diskNo: number }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const qc = useQueryClient();
  const run = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await deviceCommand(dev.apiEndpoint, dev.homeId, dev.aid, 'kinio_storage.smart_test', { disk_no: diskNo, type: 'short' });
      setMsg(t('health.smart_test_sent'));
      void qc.invalidateQueries({ queryKey: ['shadow', dev.homeId, dev.aid] });
    } catch (e) {
      const c = e instanceof KinioError ? e.code : String(e);
      setMsg(t(`dctl.err_${c}`, { defaultValue: e instanceof KinioError ? e.message : c }));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <button className="btn btn-sm" disabled={busy || dev.presence === 'offline'} onClick={() => void run()} data-smart-test-run={diskNo}>
        {t('health.smart_test_run')}
      </button>
      {msg && <span className="small muted"> {msg}</span>}
    </div>
  );
}

function DiskTable({ storage, dev, canTest, tz }: { storage: StorageHealth; dev?: VmsDevice; canTest?: boolean; tz: string }) {
  const { t } = useTranslation();
  const phys = storage.physical;
  // 手機寬度：表格自己橫向捲，不撐開頁面（SMART 欄有細節與按鈕）
  return (
    <div className="table-wrap" data-disk-table-wrap>
      <table className="grid" data-disk-table={phys ? 'physical' : 'volume'}>
        <thead>
          <tr>
            <th>#</th>
            <th>{t('health.disk_model')}</th>
            {phys && <th>{t('health.disk_kind')}</th>}
            <th>{t('health.disk_state')}</th>
            <th>SMART</th>
            {phys && <th>{t('health.disk_record')}</th>}
            <th>{phys ? t('health.physical_capacity') : t('health.pool_used')}</th>
          </tr>
        </thead>
        <tbody>
          {storage.disks.map((d: DiskInfo, i) => {
            const kind = diskKind(d);
            const smart = smartState(d.smart);
            return (
              <tr key={i} data-disk={d.device ?? d.no ?? i}>
                <td>{d.no ?? i + 1}</td>
                <td>
                  {diskModelLabel(t, d.model, kind)}
                  {/* 裝置路徑、序號收進 ⓘ（第三輪 B3-10） */}
                  {(d.device || d.serial) && (
                    <span className="muted small info-tip" title={[d.model, d.device, d.serial].filter(Boolean).join(' · ')}>
                      {' '}
                      ⓘ
                    </span>
                  )}
                </td>
                {phys && <td data-disk-kind={kind ?? ''}>{kind ? t(`health.kind_${kind}`) : '—'}</td>}
                <td data-disk-state={d.state ?? ''}>{d.state ? t(`health.dstate_${d.state}`, { defaultValue: d.state }) : '—'}</td>
                <td data-smart={smart}>
                  {t(`health.smart_${smart}`)}
                  {smart !== 'unknown' && <SmartDetail d={d} tz={tz} />}
                  {/* 只對實體碟（新形狀、有碟號）：舊形狀的一筆錄影卷沒有 disk_no，不能猜 */}
                  {dev && canTest && phys && num(d.no) && smart !== 'unknown' && d.smart_test?.state !== 'running' && (
                    <SmartTestButton dev={dev} diskNo={d.no} />
                  )}
                </td>
                {phys && (
                  <td data-disk-record={d.record === true ? 'yes' : d.record === false ? 'no' : ''}>
                    {d.record === true ? t('health.record_yes') : d.record === false ? t('health.record_no') : '—'}
                  </td>
                )}
                <td>
                  {phys ? (
                    fmtBytes(d.total_bytes ?? d.totalBytes)
                  ) : (
                    capText(t, d.total_bytes ?? d.totalBytes, d.free_bytes ?? d.freeBytes)
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** 容量一律「已用 X / 共 Y（可用 Z）」（摘要、儲存池同一種寫法；第三輪 B3-10：原本一邊寫 可用/總、一邊寫 已用/可用）。 */
export function capText(t: (k: string, o?: Record<string, unknown>) => string, total?: number, free?: number): string {
  if (total === undefined) return '—';
  if (free === undefined) return t('health.cap_total', { total: fmtBytes(total) });
  return t('health.cap_used_total_free', { used: fmtBytes(Math.max(0, total - free)), total: fmtBytes(total), free: fmtBytes(free) });
}

/** 碟的型號：USB 隨身碟常回報泛稱「Storage Media」「Mass Storage」→ 寫「USB 隨身碟」；內建 flash 的 ubi 名稱不顯示（第三輪 B3-10）。 */
export function diskModelLabel(t: (k: string) => string, model: string | undefined, kind: string | undefined | null): string {
  const m = (model ?? '').trim();
  if (kind === 'usb' && (!m || /^(usb\s*)?(storage media|mass storage|flash disk|disk)$/i.test(m))) return t('health.usb_stick');
  if (kind === 'flash' && (!m || /^ubi\d/i.test(m))) return t('health.kind_flash');
  return m || '—';
}

function PoolTable({ s }: { s: StorageHealth }) {
  const pools: PoolRow[] = s.pools;
  const { t } = useTranslation();
  const roles = (p: PoolRow) =>
    Array.isArray(p.roles) && p.roles.length ? p.roles.map((r) => t(`health.role_${r}`, { defaultValue: r })).join(t('common.list_sep')) : '—';
  return (
    <table className="grid" data-pool-table>
      <thead>
        <tr>
          <th>{t('health.pool_role')}</th>
          <th>{t('health.pool_disk')}</th>
          <th>{t('health.pool_part')}</th>
          <th>{t('health.pool_capacity')}</th>
          <th>{t('health.pool_used')}</th>
          <th>{t('health.disk_state')}</th>
        </tr>
      </thead>
      <tbody>
        {pools.map((p, i) => {
          // 錄影池跟上方摘要同一個欄位（第十三輪 B13-07）
          const { total, free } = poolUsage(s, p);
          const usedBytes = total !== undefined && free !== undefined ? Math.max(0, total - free) : undefined;
          const kind = p.diskKind;
          return (
            <tr key={i} data-pool={p.label || p.device || i}>
              <td data-pool-roles={(p.roles ?? []).join(',')}>{roles(p)}</td>
              <td>
                {p.diskNo !== undefined ? `#${p.diskNo} ` : ''}
                {kind ? t(`health.kind_${kind}`) : '—'}
                {p.diskModel && <div className="muted small">{diskModelLabel(t, p.diskModel, kind)}</div>}
              </td>
              <td>
                {kind === 'flash' ? t('health.kind_flash') : p.label || '—'}
                {(p.device || p.mount || (kind === 'flash' && p.label)) && (
                  <span className="muted small info-tip" title={[p.label, p.device, p.mount].filter(Boolean).join(' · ')}>
                    {' '}
                    ⓘ
                  </span>
                )}
              </td>
              <td>{fmtBytes(total)}</td>
              <td>
                {capText(t, total, free)}{' '}
                {usedBytes !== undefined && total ? (
                  <span className="meter" aria-hidden>
                    <span style={{ width: `${Math.round((usedBytes / total) * 100)}%` }} />
                  </span>
                ) : null}
              </td>
              <td data-pool-in-use={p.in_use === true ? 'yes' : p.in_use === false ? 'no' : ''}>
                {p.in_use === true ? t('health.pool_in_use') : p.in_use === false ? t('health.pool_idle') : '—'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function Health({ storage, dev, canTest, tz = browserTz() }: { storage: StorageHealth; dev?: VmsDevice; canTest?: boolean; tz?: string }) {
  const { t } = useTranslation();
  return (
    <div>
      <p>
        <StorageSummary s={storage} />
      </p>
      {storage.sdLooping && (
        <p className="muted small" data-testid="sd-looping-note">
          {t('health.sd_looping_note')}
        </p>
      )}
      {/* 只有記憶卡的攝影機：摘要一行就好，不畫「錄影卷」表與位元率說明（第四輪 B4-03） */}
      {!(storage.sdCard && !storage.recordKbps) && <p className="muted small">{storage.recordKbps ? t('health.rate', { kbps: storage.recordKbps }) : t('health.no_rate')}</p>}
      {storage.physical && storage.totalBytes !== undefined && (
        <p className="muted small" data-retention-basis>
          {t('health.retention_basis', { total: fmtBytes(storage.totalBytes) })}
        </p>
      )}
      {!(storage.sdCard && !storage.disks.length) && <h3 className="section-title">{storage.physical ? t('health.disks_title') : t('health.volume_title')}</h3>}
      {storage.sdCard && !storage.disks.length ? null : storage.disks.length > 0 ? (
        <>
          <DiskTable storage={storage} dev={dev} canTest={canTest} tz={tz} />
          {!storage.physical && (
            <p className="muted small" data-storage-legacy>
              {t('health.legacy_note')}
            </p>
          )}
        </>
      ) : (
        <p className="muted">{t('health.no_disks')}</p>
      )}
      {storage.physical && (
        <>
          <h3 className="section-title">{t('health.pools_title')}</h3>
          {storage.pools.length > 0 ? <PoolTable s={storage} /> : <p className="muted">{t('health.no_pools')}</p>}
        </>
      )}
    </div>
  );
}

function Channels({ dev }: { dev: VmsDevice }) {
  const { t } = useTranslation();
  return (
    <table className="grid">
      <thead>
        <tr>
          <th>{t('devices.col_status')}</th>
          <th>{t('devices.col_name')}</th>
          <th>{t('devices.col_model')}</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {dev.children.map((c) => (
          <tr key={c.key}>
            <td>
              <span className={`dot ${c.presence}`} />
              {t(`devices.${c.presence}`)}
            </td>
            <td>
              <Link to={deviceHref(c)}>{c.name}</Link>
            </td>
            <td>{modelDisplay(t, c)}</td>
            <td>
              {c.presence === 'online' && (
                <Link className="btn btn-sm" to="/live" state={{ focus: c.key }}>
                  {t('devices.watch')}
                </Link>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** 攝影機的記憶卡錄影設定（錄影政策的「記憶卡」列就是它們，façade device_mode source = sd）。 */
const SD_POLICY_KEYS = ['kinio_recording.sd_enabled', 'kinio_recording.sd_mode'];

export function RecordingPolicy({ dev, owner }: { dev: VmsDevice; owner: boolean }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const { ask, dialog } = useConfirm();
  const [busy, setBusy] = useState(false);
  const qk = ['rec-policy', dev.homeId, dev.aid];
  const q = useQuery({
    queryKey: qk,
    queryFn: () => vms.recordingPolicy(dev.apiEndpoint, dev.homeId, dev.aid) as Promise<PolicyView>,
    refetchInterval: 5000,
    retry: false,
  });
  // 設備自己的 shadow（通道讀 <主機>:chN 自己的，攝影機讀本體）：看 kinio_recording.cloud_applied（STN-02）。
  // 頁面上層的 shadow 查詢對通道讀的是主機，key 不同避免共用快取。
  const own = useQuery({
    queryKey: ['shadow-own', dev.homeId, dev.aid],
    queryFn: () => getShadow(dev.apiEndpoint, dev.homeId, dev.aid),
    refetchInterval: 15_000,
    retry: false,
  });
  const applied = cloudAppliedOf(own.data?.state?.reported as Record<string, unknown> | undefined);
  const [err, setErr] = useState<string | null>(null);
  const put = async (body: Record<string, unknown>) => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      const confirm = recordingTargetConfirm(t, dev, q.data ?? {}, {
        deviceMode: (body.device_mode as { mode?: string } | undefined)?.mode,
        cloudEnabled: (body.cloud as { cloud_enabled?: boolean } | undefined)?.cloud_enabled,
      });
      if (confirm && !(await ask(confirm))) return;
      await vms.setRecordingPolicy(dev.apiEndpoint, dev.homeId, dev.aid, body);
    } catch (e) {
      setErr((e as { code?: string }).code ?? String(e));
    } finally {
      await qc.invalidateQueries({ queryKey: qk });
      setBusy(false);
    }
  };
  if (q.isLoading) return <p className="muted">{t('common.loading')}</p>;
  if (q.error) return <p className="error">{t('common.error')}</p>;
  const p = q.data ?? {};
  const dm = p.device_mode;
  const ui = dm ? deviceModeUi(dm) : null;
  // 不支援雲錄的機種（後端 reason = not_supported，platform d05a7e26；第四輪 B4-20）：只寫一句白話，
  // 不畫雲錄開關、方案來源、套用狀態、雲錄方式與保存天數
  const unsupported = cloudUnsupported(p.cloud);
  const cm = p.cloud && !unsupported ? cloudModeUi(p.cloud) : null;
  // 設備沒回報錄影模式、也沒設定過（不錄影的機種）：不畫一個只有「—」的下拉
  const showMode = !!dm && !(unsupported && !dm.reported && !dm.desired);
  // 錄到哪裡（記憶卡 / 錄影主機 / 雲端）× 什麼時候錄（全時 / 有動靜才錄），用詞跟設備 App 一樣（第四輪 B4-04）
  const whereLabel = dm?.source === 'sd' ? t('rpol.where_sd') : dm?.source === 'schedule' ? t('rpol.where_recorder') : t('rpol.device_mode');
  const applied$ = cloudApplyStatus(p.cloud ?? {}, applied);
  return (
    <div className="card">
      <table className="grid dinfo rpol-table">
        <thead hidden={unsupported && !showMode}>
          <tr>
            <th scope="col">{t('rpol.col_where')}</th>
            <th scope="col">{t('rpol.col_when')}</th>
          </tr>
        </thead>
        <tbody>
          {showMode && dm && (
            <tr data-testid="rpol-device">
              <th scope="row">{whereLabel}</th>
              <td>
                {owner && ui ? (
                  <select
                    className="input"
                    aria-label={whereLabel}
                    disabled={busy}
                    value={ui.value}
                    onChange={(e) => void put({ device_mode: { expected_version: dm.version, mode: e.target.value } })}
                  >
                    {!ui.value && <option value="">—</option>}
                    {/* NVR / Kinio Station 的通道：錄影方式是週排程，由平台推導（source=schedule）；「依排程」只能在設定頁逐格編排 */}
                    {ui.options.map((m) => (
                      <option key={m} value={m} disabled={m === 'scheduled'}>
                        {t(`rpol.mode_${m}`)}
                      </option>
                    ))}
                  </select>
                ) : (
                  t(`rpol.mode_${dm.reported ?? 'unknown'}`, { defaultValue: t('rpol.mode_unknown') })
                )}{' '}
                <span className={`badge dset-${dm.status === 'applied' ? 'applied' : dm.status === 'pending' ? 'pending' : 'unknown'}`} data-policy-status={dm.status}>
                  {t(`dset.${dm.status}`, { defaultValue: t('common.state_unknown') })}
                </span>
                {/* 雲端全天生效時設備本機也全天錄（ADR-003 §2.5.1）；選單是使用者自己的週排程，關掉雲錄後回到它 */}
                {ui?.cloudPolicy && (
                  <div className="muted small" data-mode-source="cloud_policy">
                    {t('rpol.cloud_policy_note')}
                  </div>
                )}
              </td>
            </tr>
          )}
          {p.cloud && unsupported && (
            <tr>
              <th scope="row">{t('rpol.cloud')}</th>
              <td data-testid="cloud-unsupported">{t('rpol.cloud_unsupported')}</td>
            </tr>
          )}
          {p.cloud && !unsupported && (
            <tr data-testid="rpol-cloud">
              <th scope="row">{t('rpol.cloud')}</th>
              <td>
                <div className="toolbar wrap rpol-cloud-line">
                  <label>
                    <input
                      type="checkbox"
                      disabled={!owner || busy}
                      checked={p.cloud.cloud_enabled !== false}
                      onChange={(e) => void put({ cloud: { cloud_enabled: e.target.checked } })}
                    />{' '}
                    {/* 勾選框只寫「開啟」（使用者的設定）；生效與設備有沒有套用另起一行「狀態」（第五輪 B5-19：不再一邊「☑ 生效中」一邊「還沒回報」） */}
                    {t('rpol.cloud_switch')}
                  </label>
                  {cm &&
                    p.cloud.cloud_enabled !== false &&
                    (owner ? (
                      <select className="input" aria-label={t('rpol.cloud_mode')} disabled={busy} value={cm.value} onChange={(e) => void put({ cloud: { cloud_mode: e.target.value } })}>
                        {cm.options.map((o) => (
                          <option key={o.mode} value={o.mode} disabled={o.disabled}>
                            {t(`rpol.mode_${o.mode}`)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span>{t(`rpol.mode_${cm.value}`)}</span>
                    ))}
                </div>
                {/* 雲端全時錄影功能尚未開放（Eddy 2026-09-28：統一測試後再開），選單灰掉 */}
                {cm?.comingSoon && p.cloud.cloud_enabled !== false && (
                  <div className="muted small" data-continuous-available="false">
                    {t('rpol.continuous_coming_soon')}
                  </div>
                )}
                {!p.cloud.effective && p.cloud.reason && (
                  <div className="muted small" data-cloud-reason={p.cloud.reason}>
                    {cloudReasonText(t, p.cloud.reason)}
                  </div>
                )}
                {(() => {
                  const src = p.cloud.source === 'exempt' ? null : cloudSourceText(t, p.cloud, i18n.language);
                  return (
                    src && (
                      <div className="small" data-testid="cloud-source">
                        {src.text}{' '}
                        {src.assign && (
                          <Link to="/billing#seats" className="btn btn-sm">
                            {t('rpol.assign_seat')}
                          </Link>
                        )}
                      </div>
                    )
                  );
                })()}
                <div className="small rpol-cloud-status" data-testid="cloud-status">
                  <span className="muted">{t('rpol.status_label')}</span> <span data-testid="cloud-platform-state">{t('rpol.platform_state', { state: cloudStateText(t, p.cloud) })}</span>
                  {!own.isLoading && p.cloud.cloud_enabled !== false && (
                    <span data-cloud-applied={applied$}>
                      {' · '}
                      <span className={`badge dset-${{ applied: 'applied', not_applied: 'pending', unknown: 'unknown' }[applied$]}`}>{t(`rpol.capplied_${applied$}`)}</span>
                      {applied && applied.retentionDays > 0 && <span className="muted"> {t('rpol.capplied_days', { count: applied.retentionDays })}</span>}
                    </span>
                  )}
                </div>
              </td>
            </tr>
          )}
          {p.cloud_retention && !unsupported && (
            <tr>
              <th scope="row">{t('rpol.retention')}</th>
              <td>
                {t('rpol.days', { count: p.cloud_retention.retention_days })} <span className="muted small">{t('rpol.follows_plan')}</span>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {exclusiveRecording(dev, p) && ui?.value === 'off' && p.cloud?.cloud_enabled === false && <p role="status">{t('rpol.no_recording')}</p>}
      {dialog}
      {!owner && <p className="muted small">{t('rpol.owner_only')}</p>}
      {err && <p className="error">{t(`rpol.err_${err}`, { defaultValue: t('common.op_failed', { code: err }) })}</p>}
    </div>
  );
}

/** 錄影片段（「有錄影」的紀錄，不是事件）：預設不列（VMS UX 評審第三輪 R3-P1-3、B3-09）。 */
export function isRecordingRow(e: { key: string; class?: string | null }): boolean {
  const c = (e.class ?? '').toLowerCase();
  const k = e.key.toLowerCase();
  // 同一類裡的異常（錄影中斷、片段上傳失敗、雲錄影故障）不是「有錄影」的紀錄：照「系統」列，不藏（device/nvr#219）
  if (RECORDING_FAULTS.has(k)) return false;
  return c === 'recording' || k === 'recording' || k.startsWith('recording.') || k.endsWith('.recording') || k.includes('recording_segment');
}

const RECORDING_FAULTS = new Set(['kinio_recording.interrupted', 'kinio_recording.segment_failed', 'kinio_recording.cloud_fault']);

/**
 * 設備 › 事件分頁：跟事件頁同一套分類（圖示、分類膠囊、警報處理），預先篩這台；預設不列錄影片段（有膠囊可勾）；
 * 上方〔在事件頁開啟〕帶 device: 篩選（SPEC DEV-05 事件分頁；VMS UX 評審第三輪 R3-P1-3、B3-09）。
 */
function DeviceEvents({ dev }: { dev: VmsDevice }) {
  const { t, i18n } = useTranslation();
  const tzOf = useTzResolver();
  const [to] = useState(() => new Date());
  const [off, setOff] = useState<Set<string>>(() => new Set(['recording']));
  const from = new Date(to.getTime() - 24 * 3600_000);
  const q = useQuery({
    queryKey: ['dev-events', dev.homeId, dev.aid],
    queryFn: () => vms.events(dev.apiEndpoint, dev.homeId, { device: dev.aid, from: from.toISOString(), to: to.toISOString(), limit: 100 }),
    retry: false,
  });
  if (q.isLoading) return <p className="muted">{t('common.loading')}</p>;
  if (q.error) return <p className="error">{t('common.error')}</p>;
  const all: VmsFacadeEvent[] = q.data?.data ?? [];
  const catOf = (e: VmsFacadeEvent): string => (isRecordingRow(e) ? 'recording' : classify(e.key, e.class ?? undefined));
  const counts = new Map<string, number>();
  for (const e of all) counts.set(catOf(e), (counts.get(catOf(e)) ?? 0) + 1);
  const cats = [...counts.keys()].sort((a, b) => (a === 'recording' ? 1 : b === 'recording' ? -1 : 0));
  const list = all.filter((e) => !off.has(catOf(e)));
  const eventsHref = `/events?tab=all&q=${encodeURIComponent(deviceToken(dev.aid, dev.name))}`;
  return (
    <div data-testid="ddev-events">
      <div className="toolbar wrap">
        {/* 分類膠囊數的是這台過去 24 小時的設備事件（跟事件頁的索引不同來源，第十二輪 B12-07） */}
        <span className="muted small" data-testid="ddev-ev-window">
          {t('ddev.ev_window')}
        </span>
        {cats.map((c) => (
          <button
            key={c}
            className={'chip' + (off.has(c) ? '' : ' on')}
            aria-pressed={!off.has(c)}
            onClick={() => setOff((s) => (s.has(c) ? new Set([...s].filter((x) => x !== c)) : new Set([...s, c])))}
            data-cat={c}
          >
            <span aria-hidden>{c === 'recording' ? '🎞' : eventIcon(c as VmsEventClass)}</span> {c === 'recording' ? t('ddev.ev_recording') : t(`events.class_${c}`)}{' '}
            <span className="muted">{counts.get(c)}</span>
          </button>
        ))}
        <span className="spacer" />
        <Link className="btn btn-sm" to={eventsHref} data-testid="ddev-events-open">
          {t('ddev.ev_open_in_events')}
        </Link>
      </div>
      {/* 「系統」是什麼（第四輪 R4-P1-8：36 列「系統 開始／結束」沒說明） */}
      {list.some((e) => catOf(e) === 'system') && (
        <p className="muted small" data-testid="ddev-ev-system-hint">
          {t('ddev.ev_system_hint')}
        </p>
      )}
      {!list.length ? (
        <p className="muted">{all.length ? t('ddev.ev_only_recording') : t('ddev.no_events')}</p>
      ) : (
        <ul className="event-list">
          {pairPhases(list).map(({ e, durSec, ep }) => {
            const cls = catOf(e);
            return (
              <li key={e.event_id} className={`event-row level-${cls}`} data-cat={cls}>
                <span className="event-icon" aria-hidden>
                  {cls === 'recording' ? '🎞' : eventIcon(cls as VmsEventClass, e.class ?? undefined)}
                </span>
                <div className="event-main">
                  <strong>{cls === 'system' && eventName(t, e.key, e.class, e.fields, e.thing?.device_ref) === t('events.class_system') ? t('ddev.ev_system_generic') : eventName(t, e.key, e.class, e.fields, e.thing?.device_ref)}</strong>
                  {/* 開始 / 結束配成一列時寫「（持續 N 秒）」，不再分兩列（第五輪 R5-P2-2） */}
                  {durSec !== undefined ? (
                    <span className="muted small" style={{ marginLeft: 6 }} data-testid="ddev-ev-dur">
                      {t('ddev.ev_lasted', { dur: durationText(t, durSec) })}
                    </span>
                  ) : (
                    ep && <EpisodeNote ep={ep} />
                  )}
                  <div className="muted small">{formatDateTime(Date.parse(e.occurred_at) / 1000, tzOf(dev.homeId), i18n.language)}</div>
                </div>
                {cls === 'alarm' && <AlarmActions menu homeId={dev.homeId} eventId={e.event_id} />}
                <Link className="btn btn-sm" to={`/events/${encodeURIComponent(dev.homeId)}/${encodeURIComponent(e.event_id)}`} state={{ ev: e, device: dev.key }}>
                  {t('evd.open')}
                </Link>
                {dev.isCamera && (
                  <Link className="btn btn-sm" to={playbackHref(dev.key, Date.parse(e.occurred_at) / 1000, { event: e.event_id })}>
                    {t('live.watch_playback')}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Links({ dev }: { dev: VmsDevice }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div>
      <p className="muted">{t('ddev.links_hint')}</p>
      <button className="btn" onClick={() => setOpen(true)}>
        {t('link.button')}
      </button>
      <LinkDialog sensor={open ? dev : null} close={() => setOpen(false)} />
    </div>
  );
}

/** 重新開機（維護區，第 2 級確認；SPEC DEV-05「危險動作只在一處」）。 */
function Reboot({ dev }: { dev: VmsDevice }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const { ask, dialog } = useConfirm();
  const run = async () => {
    if (!(await ask({ title: t('dctl.reboot_confirm', { name: dev.name }), action: t('common.action_reboot') }))) return;
    setBusy(true);
    setMsg(null);
    try {
      await deviceCommand(dev.apiEndpoint, dev.homeId, dev.aid, 'kinio_system.reboot');
      setMsg(t('dctl.sent'));
    } catch (e) {
      const c = e instanceof KinioError ? e.code : String(e);
      setMsg(t(`dctl.err_${c}`, { defaultValue: t('common.error') }));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="card" data-testid="ddev-reboot">
      <h3>{t('dctl.reboot')}</h3>
      <p className="muted small">{t('ddev.reboot_hint')}</p>
      <button className="btn" disabled={busy || dev.presence === 'offline'} onClick={() => void run()} data-cmd="kinio_system.reboot">
        {t('dctl.reboot')}
      </button>
      {msg && <span className="small muted"> {msg}</span>}
      {dialog}
    </div>
  );
}

function Manage({ dev }: { dev: VmsDevice }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const confirmText = dev.aid.slice(-4);
  const go = async () => {
    setBusy(true);
    setErr(null);
    try {
      await deviceApi.unbind(dev.apiEndpoint, dev.homeId, dev.aid);
      await qc.invalidateQueries({ queryKey: ['home', dev.homeId] });
      nav('/devices', { replace: true });
    } catch (e) {
      setErr((e as { code?: string }).code ?? String(e));
      setBusy(false);
    }
  };
  return (
    <>
    {changeWifiOffered(dev) && (
      <div className="card" data-testid="ddev-change-wifi">
        <h3>{t('ddev.change_wifi')}</h3>
        <p className="muted">{t('ddev.change_wifi_app')}</p>
      </div>
    )}
    <Reboot dev={dev} />
    <div className="card danger-zone">
      <h3>{t('unbind.title')}</h3>
      <p>{t('unbind.explain')}</p>
      <ul className="muted">
        <li>{t('unbind.reset')}</li>
        <li>{t('unbind.history')}</li>
        {dev.children.length > 0 && <li>{t('unbind.children', { count: dev.children.length })}</li>}
      </ul>
      <button className="btn btn-danger" onClick={() => setOpen(true)}>
        {t('unbind.button')}
      </button>
      <Dialog open={open} title={t('unbind.title')} onClose={() => setOpen(false)}>
        <p>{t('unbind.confirm', { name: dev.name, code: confirmText })}</p>
        <input className="input" value={typed} onChange={(e) => setTyped(e.target.value)} aria-label={t('unbind.type_code')} placeholder={confirmText} />
        {err && <p className="error">{t(`unbind.err_${err}`, { defaultValue: t('common.op_failed', { code: err }) })}</p>}
        <div className="dialog-actions">
          <button className="btn" onClick={() => setOpen(false)}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-danger" disabled={busy || typed.trim().toUpperCase() !== confirmText.toUpperCase()} onClick={() => void go()}>
            {busy ? t('common.loading') : t('unbind.button')}
          </button>
        </div>
      </Dialog>
    </div>
    </>
  );
}
